import type { GUID } from "@pdfking/tfunc"
import type { AsyncSequence } from "@pdfking/tfunc"
import {
  Broadcast,
  FullMethodDispatcher,
  ProcessIterable,
  UUID,
} from "@pdfking/tfunc"

import type { MessageRouter } from "../types/MessageRouter"
import { ReceiverAction } from "../types/ReceiverAction"
import type { ReceiverInput } from "../types/ReceiverInput"
import type { ReceiverMessage } from "../types/ReceiverMessage"
import type { ReceiverOutput } from "../types/ReceiverOutput"
import type { WorkerReceiverAPI } from "../types/WorkerReceiver"

const StreamToReceiver = <T, R>(
  stream: AsyncSequence<T>,
  { send }: WorkerReceiverAPI<T, R>,
  channel_id: GUID
): Promise<void> =>
  ProcessIterable(
    stream,
    data =>
      send({
        enum: ReceiverAction.YIELD,
        channel_id,
        data,
      }),
    error =>
      send({
        enum: ReceiverAction.STOP,
        channel_id,
        error,
      })
  )

const forward_to = async <T, R>(
  stream: ReceiverInput<T>,
  worker: WorkerReceiverAPI<T, R>,
  channel_id: GUID,
  ...extra: ReceiverMessage<T>[]
) => {
  for (const message of extra) {
    worker.send(message)
  }

  await StreamToReceiver(stream, worker, channel_id)
}

export const WorkerForward = <T, R>(
  worker: WorkerReceiverAPI<T, R>,
  forward: MessageRouter<T, R>
): MessageRouter<T, R> => {
  const channels: Map<GUID, ReturnType<typeof Broadcast<R | T>>> = new Map()

  const get_broadcast = (channel: GUID) => {
    let current = channels.get(channel)
    if (!current) channels.set(channel, (current = Broadcast<R | T>()))
    return current
  }

  worker.on(
    FullMethodDispatcher<[ReceiverMessage<R | T>], void>({
      [ReceiverAction.START]: ({ request_channel, response_channel }) => {
        const stream = get_broadcast(request_channel).subscribe()

        void forward_to(
          forward(stream as ReceiverInput<T>) as ReceiverInput<T>,
          worker,
          response_channel
        )
      },

      [ReceiverAction.YIELD]: ({ channel_id, data }) =>
        channels.get(channel_id)!.push(data),

      [ReceiverAction.STOP]: ({ channel_id, error }) => {
        channels.get(channel_id)!.stop(error)
        channels.delete(channel_id)
      },
    })
  )

  return async function* (request: ReceiverInput<T>): ReceiverOutput<R> {
    //  new request is incoming, we forward the request to the worker and return a response.

    const request_channel = UUID()
    const response_channel = UUID()

    const stream = get_broadcast(response_channel).subscribe()

    void forward_to(request, worker, request_channel, {
      enum: ReceiverAction.START,
      request_channel,
      response_channel,
    })

    yield* stream as ReceiverOutput<R>
  }
}
